import { useEffect, useMemo, useState } from 'react'
import { Box, Grid } from '@mui/material'
import { Button } from '../../index'
import { DealerLocatorList } from './DealerLocatorList'
import { DealerLocatorMap } from './DealerLocatorMap'
import { DealerFilters } from './DealerFilters'
import { Loader } from '../../indicators'
import { isArray, isEmpty, noop } from 'lodash'
import { useApiDataDigest } from '../../../local'
import { useI18n } from '@ggs/i18n'
import icons from '../../icons'
import { useLocatorApi } from '../../../local/hooks'
import { resetSelectedSearchResult, useDispatch } from '@ggs/store'
import './DealerLocatorView.scss'

/**
 * @typedef {import('../../../definitions').DealerEntity} DealerEntity
 */

/**
 * Dealer locator view.
 *
 * @return {JSX.Element}
 */
export const DealerLocatorView = () => {
  const { t } = useI18n()
  const { digestDealersList, composeDealersLeafletPlaces } = useApiDataDigest()
  const { getDealers } = useLocatorApi()
  const dispatch = useDispatch()

  /**
   * @type {DealerEntity[]}
   */
  const defaultDealers = null
  const [dealers, setDealers] = useState(defaultDealers)
  const [loading, setLoading] = useState(false)
  const [showMap, setShowMap] = useState(false)

  /**
   * @typedef {Object} DealersFilters
   * @property {string} country
   * @property {string=} stateProvince
   */

  /**
   * Compose dealers filter path string params for API request.
   * @param {DealersFilters} filters
   * @return {string}
   */
  const composeDealersFilterParams = ({ country, stateProvince = '' }) => {
    /**
     *  @type {Object<string, string>}
     */
    const countryMap = {
      CA: 'CANADA',
      US: 'USA',
      SA: 'SAUDI ARABIA',
    }

    const countryCode = countryMap[country] || country
    const stateProvinceCode = stateProvince === 'all' || isEmpty(stateProvince) ? '' : `${stateProvince}/`

    return `${stateProvinceCode}${countryCode}`
  }

  /**
   * Resolve dealers data from Dealers API.
   * @param {DealersFilters} filters
   * @param {function} setSubmitting
   * @return {Promise<void>}
   */
  const resolveDealersData = async (filters, setSubmitting = noop) => {
    const pathParams = composeDealersFilterParams(filters)
    const dealerResults = await getDealers(pathParams)
    setSubmitting(false)

    if (isArray(dealerResults)) {
      // On updated results set, reset the previously selected result.
      dispatch(resetSelectedSearchResult())
      setDealers(digestDealersList(dealerResults))
    }

    setLoading(false)
  }

  useEffect(() => {
    resolveDealersData({
      country: 'CA',
      stateProvince: '',
    }).then(() => {})
  }, [])

  const places = useMemo(() => {
    if (dealers) {
      return composeDealersLeafletPlaces(dealers)
    }
  }, [dealers])

  if (loading || !dealers) {
    return <Loader />
  }

  return (
    <Box className="dealer-locator">
      <Grid className="dealer-locator__container">
        <Grid className="dealer-locator__list">
          {isEmpty(dealers) ? (
            <div>{t('No results found')}</div>
          ) : (
            <DealerLocatorList dealers={dealers} />
          )}
        </Grid>
        <Grid className="dealer-locator__map-wrapper">
          <DealerFilters resolveData={resolveDealersData} />
          <div className="dealer-locator__toggle-map">
            <Button
              onClick={() => setShowMap(!showMap)}
              color="primary"
              classes={{
                root: 'btn--with-icon',
              }}
            >
              <>
                <span className="btn__icon">{icons.SearchIcon}</span>
                <span className="btn__text">{t('Find on map')}</span>
              </>
            </Button>
          </div>
          <div
            className={
              showMap ? 'dealer-locator__map dealer-locator__map--visible' : 'dealer-locator__map'
            }
          >
            {!isEmpty(dealers) && <DealerLocatorMap places={places} />}
          </div>
        </Grid>
      </Grid>
    </Box>
  )
}
